import { Link, createFileRoute } from '@tanstack/react-router';

import { NotFoundState } from '@/shared/components/NotFoundState';
import {
  BoardPage,
  boardLabelNarrowing,
  boardSearchSchema,
} from '@/features/tasks';
import { serializeLabelNarrowing } from '@/features/labels';

export const Route = createFileRoute(
  '/workspaces/$workspaceId/workflows/$workflowKey/board',
)({
  // Board view mode, the open task, and its selected tab are shareable and
  // survive a reload, so they live in the address rather than in a store.
  // Which workflow's board this is comes from `$workflowKey` instead: it names
  // a resource, so an unknown key is not-found rather than a fallback.
  validateSearch: boardSearchSchema,
  // Deliberately no prefetching loader. The board renders its own pending
  // state, and a loader that refetches the aggregate on every arrival would
  // discard the task a create seeds into the cache — switching boards and
  // back would silently lose it.
  component: BoardRoute,
  notFoundComponent: WorkflowNotFound,
});

export function BoardRoute() {
  const { workspaceId, workflowKey } = Route.useParams();
  const search = Route.useSearch();
  const { task, tab, preview, state, q, diagram, create } = search;
  const navigate = Route.useNavigate();

  // Read here, applied in the page. The route module holds no rules and runs no
  // query — it turns the address into props and turns a change back into an
  // address, which is the whole of its job.
  const labels = boardLabelNarrowing(search);

  return (
    <BoardPage
      workspaceId={workspaceId}
      workflowKey={workflowKey}
      openTaskId={task}
      openTab={tab}
      previewPath={preview}
      createRequested={create}
      onCreateDismissed={() => {
        // Out of the address once the board has opened its draft: the draft
        // is what survives a reload (spec 121), and a parameter that stayed
        // would reopen the dialog the person had folded away.
        void navigate({
          search: (prev) => ({ ...prev, create: undefined }),
          replace: true,
        });
      }}
      showDiagram={diagram}
      onShowDiagramChange={(next) => {
        // Into the address, so the view can be shared as it is being seen
        // (spec 094 FR-012). `undefined` rather than `false` when hidden: the
        // default is hidden, and an address carrying `diagram=false` would claim
        // the reader chose something they never did.
        void navigate({
          search: (prev) => ({ ...prev, diagram: next ? true : undefined }),
        });
      }}
      query={q}
      onQueryChange={(next) => {
        void navigate({ search: (prev) => ({ ...prev, q: next }) });
      }}
      stateFilter={state}
      onStateFilterChange={(next) => {
        void navigate({ search: (prev) => ({ ...prev, state: next }) });
      }}
      labelNarrowing={labels}
      onLabelNarrowingChange={(next) => {
        void navigate({
          // `undefined` drops the parameter rather than writing `labels=`: an
          // unfiltered board says so by carrying nothing.
          search: (prev) => ({
            ...prev,
            labels: serializeLabelNarrowing(next),
          }),
        });
      }}
      onOpenTask={(taskId) => {
        // No morph from the card into the drawer (2026-10-05): it carried
        // whatever the card showed at that instant, and read as a glitch.
        void navigate({
          search: (prev) => ({
            ...prev,
            task: taskId,
            // An open preview belongs to the task it was opened from (spec
            // 140 FR-064). Carried across a task switch it would leave the
            // panel showing a file linked from the task you just left — or,
            // when the new task cannot reach it, a "renamed, moved or
            // deleted" state beside an unrelated task. Cleared here for the
            // same reason `onCloseTask` strips it; re-opening the task it
            // came from does not resurrect it, because nothing re-derives it
            // from the previous address.
            preview: prev.task === taskId ? prev.preview : undefined,
          }),
        });
      }}
      onTabChange={(next, options) => {
        // `replace` when the drawer is writing back the tab it landed on
        // (spec 095 FR-004): that is the address catching up with what is
        // shown, not a step the reader took, so Back must not undo it.
        void navigate({
          search: (prev) => ({ ...prev, tab: next }),
          replace: options?.replace ?? false,
        });
      }}
      onCloseTask={() => {
        // `tab` is meaningless without `task` and is stripped with it, which is
        // the shape `boardSearchSchema` documents. The preview opened from
        // inside the drawer goes with it for the same reason.
        void navigate({
          search: (prev) => ({
            ...prev,
            task: undefined,
            tab: undefined,
            preview: undefined,
          }),
        });
      }}
      onPreviewClose={() => {
        // Only the preview leaves: the drawer and its tab stay as they are.
        void navigate({
          search: (prev) => ({ ...prev, preview: undefined }),
        });
      }}
    />
  );
}

export function WorkflowNotFound() {
  const { workspaceId } = Route.useParams();
  return (
    <NotFoundState
      title="Workflow not found"
      description="This workspace has no workflow with that key. It may have been renamed or removed since the link was shared."
      // The list is where you pick a workflow that does exist — the same
      // reasoning the board page already applies when a key resolves to
      // nothing mid-session.
      action={
        <Link
          to="/workspaces/$workspaceId/workflows"
          params={{ workspaceId }}
          className="text-primary focus-visible:ring-ring rounded-sm text-sm hover:underline focus-visible:ring-3 focus-visible:outline-none"
        >
          Go to all workflows
        </Link>
      }
    />
  );
}
